import React from 'react';
import { Form, Input, Select, Row, Col, Button, Card, Divider, Tag, Tooltip, Radio } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { SOP_CONSTANTS, targetTypes } from '../../constants/sopConstants';
import EmployeeSelect from '../common/EmployeeSelect';
import GroupSelect from '../common/GroupSelect';

const { Option } = Select;

// 辅助函数：按标签组对标签进行分类
const groupTagsByGroup = (tags = []) => {
  const groupedTags = {};
  
  // 如果标签为空，返回空数组
  if (!tags || tags.length === 0) {
    return [];
  }
  
  // 将标签按组分类
  tags.forEach(tag => {
    // 确保tag是有效对象
    if (!tag) return;
    
    const groupId = tag.groupId || 'default';
    const groupName = tag.groupName || '默认分组';
    
    if (!groupedTags[groupId]) {
      groupedTags[groupId] = {
        groupId,
        groupName,
        tags: []
      };
    }
    
    groupedTags[groupId].tags.push(tag);
  });
  
  // 转换为数组并排序
  return Object.values(groupedTags).sort((a, b) => a.groupName.localeCompare(b.groupName));
};

const TemplateForm = ({ 
  form, 
  tags, 
  onTargetTypeChange, 
  isTemplateActive = false, 
  hideTagSettings = false,
  isScheduled = false,
  groups = []
}) => {
  // 获取表单字段值
  const targetType = Form.useWatch('targetType', form) || 'CUSTOMER';
  // 获取客户目标类型值（用于定时SOP）
  const customerTargetType = Form.useWatch('customerTargetType', form) || 'TAG';
  
  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        targetType: SOP_CONSTANTS.TargetType.CUSTOMER,
      }}
      disabled={isTemplateActive}
      style={{ maxWidth: '900px' }}
    >
      {/* 隐藏的编码字段，保留在表单中但不显示 */}
      <Form.Item
        name="code"
        hidden={true}
      >
        <Input />
      </Form.Item>
      
      <Row gutter={24}>
        <Col span={24}>
          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入推送名称' }]}
          >
            <Input placeholder="请输入推送名称" style={{ maxWidth: '400px' }} />
          </Form.Item>
        </Col>
      </Row>
      
      <Form.Item
        name="description"
        label="描述"
        hidden={true}
      >
        <Input.TextArea rows={3} placeholder="请输入推送描述" style={{ maxWidth: '800px' }} />
      </Form.Item>
      
      {/* 当不是定时SOP时，才显示模板级别的目标类型和相关设置 */}
      {!isScheduled && (
        <>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="targetType"
                label="目标类型"
                rules={[{ required: true, message: '请选择目标类型' }]}
              >
                <Select 
                  placeholder="请选择目标类型"
                  onChange={onTargetTypeChange}
                  style={{ maxWidth: '300px' }}
                >
                  {targetTypes.map(type => (
                    <Option key={type.value} value={type.value}>{type.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          {/* 企业微信成员选择 - 只对标准SOP适用 */}
          {targetType === 'CUSTOMER' && (
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  name="employeeIds"
                  label={
                    <span>
                      指定企业微信成员 <span style={{ color: '#ff4d4f' }}>*</span>
                      <Tooltip title="必须选择至少一个已启用的企业微信成员，只对这些成员的客户执行SOP">
                        <InfoCircleOutlined style={{ marginLeft: 8, cursor: 'pointer', color: '#1890ff' }} />
                      </Tooltip>
                    </span>
                  }
                  rules={[{ required: true, message: '请至少选择一个企业微信成员' }]}
                >
                  <EmployeeSelect 
                    multiple 
                    placeholder="请选择或搜索企业微信成员（必选，可多选）" 
                    style={{ maxWidth: '600px' }}
                    disabled={isTemplateActive}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          
          {/* 标准SOP的触发规则 */}
          {targetType === 'CUSTOMER' && !hideTagSettings && (
            <div className="trigger-rules" style={{ maxWidth: '850px' }}>
              <Divider orientation="left">
                触发规则（标签变更触发）
                <Tooltip title='多个触发规则之间为"或"关系，满足任一条件即可触发SOP流程'>
                  <InfoCircleOutlined style={{ marginLeft: 8, cursor: 'pointer', color: '#1890ff' }} />
                </Tooltip>
              </Divider>
              <Form.List name="triggerRules">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <Card 
                        key={field.key} 
                        size="small" 
                        title={`触发规则 ${index + 1}`}
                        style={{ marginBottom: 16 }}
                        extra={
                          <Button 
                            type="link" 
                            danger 
                            icon={<DeleteOutlined />} 
                            onClick={() => remove(field.name)}
                            disabled={isTemplateActive}
                          />
                        }
                      >
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              {...field}
                              name={[field.name, 'tagId']}
                              label="触发标签"
                              rules={[{ required: true, message: '请选择触发标签' }]}
                            >
                              <Select 
                                placeholder="请选择触发标签" 
                                showSearch 
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                  // 处理OptGroup标签
                                  if (option.type === 'group') {
                                    return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                  }
                                  // 处理Option标签
                                  return option.children ? 
                                    option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0 : 
                                    false;
                                }}
                                style={{ width: '100%', maxWidth: '350px' }}
                                dropdownMatchSelectWidth={false}
                                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                notFoundContent={tags.length === 0 ? "加载中..." : "未找到匹配标签"}
                              >
                                {groupTagsByGroup(tags).map(group => (
                                  <Select.OptGroup key={group.groupId} label={group.groupName}>
                                    {group.tags.map(tag => (
                                      <Option key={tag.id} value={tag.id}>
                                        {tag.tagName || tag.name} 
                                        {tag.source === 'WEWORK' && <Tag color="blue" style={{ marginLeft: 8 }}>企微</Tag>}
                                      </Option>
                                    ))}
                                  </Select.OptGroup>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              {...field}
                              name={[field.name, 'operation']}
                              label="操作"
                              rules={[{ required: true, message: '请选择操作' }]}
                            >
                              <Select 
                                placeholder="请选择操作"
                                style={{ width: '100%', maxWidth: '200px' }}
                              >
                                <Option value="ADD">添加标签</Option>
                                {/* <Option value="REMOVE">移除标签</Option> */}
                                {/* <Option value="ANY_CHANGE">任何变化</Option> */}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                        
                        {/* 添加排除标签选择 */}
                        <Form.Item
                          {...field}
                          name={[field.name, 'excludeTagIds']}
                          label={
                            <span>
                              排除标签
                              <Tooltip title="拥有这些标签的客户即使满足触发条件，也不会触发SOP">
                                <InfoCircleOutlined style={{ marginLeft: 8, cursor: 'pointer', color: '#1890ff' }} />
                              </Tooltip>
                            </span>
                          }
                        >
                          <Select 
                            placeholder="请选择排除标签（可多选）" 
                            mode="multiple"
                            showSearch 
                            allowClear
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                              // 处理OptGroup标签
                              if (option.type === 'group') {
                                return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                              }
                              // 处理Option标签
                              return option.children ? 
                                option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0 : 
                                false;
                            }}
                            style={{ width: '100%', maxWidth: '800px' }}
                            dropdownMatchSelectWidth={false}
                            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                            notFoundContent={tags.length === 0 ? "加载中..." : "未找到匹配标签"}
                          >
                            {groupTagsByGroup(tags).map(group => (
                              <Select.OptGroup key={group.groupId} label={group.groupName}>
                                {group.tags.map(tag => (
                                  <Option key={tag.id} value={tag.id}>
                                    {tag.tagName || tag.name} 
                                    {tag.source === 'WEWORK' && <Tag color="blue" style={{ marginLeft: 8 }}>企微</Tag>}
                                  </Option>
                                ))}
                              </Select.OptGroup>
                            ))}
                          </Select>
                        </Form.Item>
                      </Card>
                    ))}
                    
                    <Form.Item>
                      <Button 
                        type="dashed" 
                        onClick={() => add({ tagId: undefined, operation: 'ADD', excludeTagIds: [] })} 
                        style={{ width: '180px' }}
                        icon={<PlusOutlined />}
                        disabled={isTemplateActive}
                      >
                        添加触发规则
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </div>
          )}
          
          {/* 客户群目标设置 - 只在非定时SOP的GROUP目标类型下显示 */}
          {targetType === 'GROUP' && (
            <div className="group-settings" style={{ maxWidth: '850px' }}>
              <Divider orientation="left">
                客户群目标设置
                <Tooltip title="选择要执行SOP的客户群">
                  <InfoCircleOutlined style={{ marginLeft: 8, cursor: 'pointer', color: '#1890ff' }} />
                </Tooltip>
              </Divider>
              
              <Form.Item
                name="targetIds"
                label="选择客户群"
                rules={[{ required: true, message: '请至少选择一个客户群' }]}
              >
                <GroupSelect
                  groups={groups}
                  mode="multiple"
                  placeholder="请选择客户群（可多选）"
                  style={{ width: '100%' }}
                  disabled={isTemplateActive}
                  valueKey="id"
                  labelKey="name"
                  valueType="string"
                  showOwner={false}
                />
              </Form.Item>
            </div>
          )}
        </>
      )}
      
      {/* 在定时SOP模式下，添加隐藏但必要的字段 */}
      {isScheduled && (
        <>
          {/* 隐藏目标类型字段，但在表单中保留 */}
          <Form.Item name="targetType" hidden>
            <Input />
          </Form.Item>
          
          {/* 隐藏企业微信成员字段，但在表单中保留 */}
          <Form.Item name="employeeIds" hidden>
            <Input />
          </Form.Item>
        </>
      )}
    </Form>
  );
};

export default TemplateForm; 